export const OPERATIONAL_CONTEXT_TYPES = [
  'aquaculture',
  'marine_environment',
  'industrial',
  'agriculture',
  'research'
] as const;

export type OperationalContextType = typeof OPERATIONAL_CONTEXT_TYPES[number];

export interface OperationalContextProfile {
  type: OperationalContextType;
  label: string;
  description: string;
  areaLabel: string;
  areaCountSingularLabel: string;
  areaPluralLabel: string;
  subjectLabel: string;
  categoryLabel: string;
  phaseLabel: string;
  phaseOptions: Array<{ value: string; label: string }>;
  locationTypeOptions: Array<{ value: string; label: string }>;
  areaTypeOptions: Array<{ value: string; label: string }>;
  sensorTypeOptions: Array<{ value: string; label: string; unit?: string; min?: number | null; max?: number | null }>;
  categoryPhaseFallback: string;
}

export const OPERATIONAL_CONTEXT_PROFILES: Record<OperationalContextType, OperationalContextProfile> = {
  aquaculture: {
    type: 'aquaculture',
    label: 'Aquacultura',
    description: 'Produção aquícola, espécimes, tanques, patologias e comportamento.',
    areaLabel: 'Área operacional',
    areaCountSingularLabel: 'área',
    areaPluralLabel: 'áreas',
    subjectLabel: 'Espécime',
    categoryLabel: 'Espécie',
    phaseLabel: 'Fase biológica',
    phaseOptions: [
      { value: 'egg', label: 'Ovo' },
      { value: 'larval', label: 'Larvar' },
      { value: 'juvenile', label: 'Juvenil' },
      { value: 'pre-grow', label: 'Pré-engorda' },
      { value: 'adult', label: 'Adulto' }
    ],
    locationTypeOptions: [
      { value: 'hatchery', label: 'Hatchery' },
      { value: 'aquaculture-farm', label: 'Unidade aquícola' },
      { value: 'pilot-facility', label: 'Instalação piloto' },
      { value: 'research-lab', label: 'Laboratório aquícola' }
    ],
    areaTypeOptions: [
      { value: 'tank', label: 'Tanque' },
      { value: 'raceway', label: 'Raceway' },
      { value: 'production-line', label: 'Linha de produção aquícola' },
      { value: 'nursery', label: 'Berçário' },
      { value: 'quarantine', label: 'Área de quarentena' }
    ],
    sensorTypeOptions: [
      { value: 'temperature', label: 'Temperatura', unit: '°C', min: 16, max: 24 },
      { value: 'ph', label: 'pH', unit: 'pH', min: 7.2, max: 8.4 },
      { value: 'dissolved_oxygen', label: 'O₂ dissolvido', unit: 'mg/L', min: 5.5, max: null },
      { value: 'redox', label: 'Redox / ORP', unit: 'mV', min: 250, max: 400 },
      { value: 'salinity', label: 'Salinidade', unit: 'ppt' },
      { value: 'turbidity', label: 'Turbidez', unit: 'NTU' }
    ],
    categoryPhaseFallback: 'Espécie/fase por definir'
  },
  marine_environment: {
    type: 'marine_environment',
    label: 'Ambiente marinho',
    description: 'Observação de organismos, habitats e alterações no meio marinho.',
    areaLabel: 'Zona de observação',
    areaCountSingularLabel: 'zona',
    areaPluralLabel: 'zonas',
    subjectLabel: 'Organismo ou habitat',
    categoryLabel: 'Espécie/habitat',
    phaseLabel: 'Campanha ou período',
    phaseOptions: [
      { value: 'baseline', label: 'Caracterização inicial' },
      { value: 'campaign', label: 'Campanha de observação' },
      { value: 'monitoring', label: 'Monitorização contínua' },
      { value: 'analysis', label: 'Análise' },
      { value: 'validation', label: 'Validação' }
    ],
    locationTypeOptions: [
      { value: 'observation-station', label: 'Estação de observação' },
      { value: 'coastal-site', label: 'Zona costeira' },
      { value: 'offshore-site', label: 'Zona offshore' },
      { value: 'marine-lab', label: 'Laboratório marinho' }
    ],
    areaTypeOptions: [
      { value: 'observation-zone', label: 'Zona de observação' },
      { value: 'transect', label: 'Transecto' },
      { value: 'sampling-station', label: 'Estação de amostragem' },
      { value: 'habitat', label: 'Habitat monitorizado' },
      { value: 'marine-sector', label: 'Setor marinho' }
    ],
    sensorTypeOptions: [
      { value: 'temperature', label: 'Temperatura', unit: '°C' },
      { value: 'salinity', label: 'Salinidade', unit: 'ppt' },
      { value: 'dissolved_oxygen', label: 'O₂ dissolvido', unit: 'mg/L' },
      { value: 'turbidity', label: 'Turbidez', unit: 'NTU' },
      { value: 'depth', label: 'Profundidade', unit: 'm' },
      { value: 'current_speed', label: 'Velocidade da corrente', unit: 'm/s' }
    ],
    categoryPhaseFallback: 'Espécie/habitat por definir'
  },
  industrial: {
    type: 'industrial',
    label: 'Inspeção industrial',
    description: 'Inspeção visual de equipamentos, componentes, processos e anomalias.',
    areaLabel: 'Área de inspeção',
    areaCountSingularLabel: 'área',
    areaPluralLabel: 'áreas',
    subjectLabel: 'Ativo ou componente',
    categoryLabel: 'Classe de ativo',
    phaseLabel: 'Fase operacional',
    phaseOptions: [
      { value: 'planning', label: 'Planeamento' },
      { value: 'in-operation', label: 'Em operação' },
      { value: 'scheduled-stop', label: 'Paragem programada' },
      { value: 'maintenance', label: 'Manutenção' },
      { value: 'post-intervention', label: 'Validação pós-intervenção' }
    ],
    locationTypeOptions: [
      { value: 'factory', label: 'Fábrica' },
      { value: 'industrial-facility', label: 'Unidade industrial' },
      { value: 'workshop', label: 'Oficina' },
      { value: 'inspection-site', label: 'Local de inspeção' },
      { value: 'warehouse', label: 'Armazém' }
    ],
    areaTypeOptions: [
      { value: 'inspection-area', label: 'Área de inspeção' },
      { value: 'production-line', label: 'Linha de produção' },
      { value: 'machine', label: 'Máquina' },
      { value: 'asset', label: 'Ativo' },
      { value: 'component', label: 'Componente' },
      { value: 'work-cell', label: 'Célula de trabalho' }
    ],
    sensorTypeOptions: [
      { value: 'temperature', label: 'Temperatura', unit: '°C' },
      { value: 'vibration', label: 'Vibração', unit: 'mm/s' },
      { value: 'pressure', label: 'Pressão', unit: 'bar' },
      { value: 'current', label: 'Corrente elétrica', unit: 'A' },
      { value: 'power', label: 'Potência', unit: 'kW' },
      { value: 'humidity', label: 'Humidade', unit: '%' }
    ],
    categoryPhaseFallback: 'Ativo/fase por definir'
  },
  agriculture: {
    type: 'agriculture',
    label: 'Agricultura',
    description: 'Monitorização visual de culturas, parcelas e condições agronómicas.',
    areaLabel: 'Parcela ou zona',
    areaCountSingularLabel: 'parcela/zona',
    areaPluralLabel: 'parcelas/zonas',
    subjectLabel: 'Cultura',
    categoryLabel: 'Cultura/variedade',
    phaseLabel: 'Fase fenológica',
    phaseOptions: [
      { value: 'germination', label: 'Germinação' },
      { value: 'vegetative', label: 'Desenvolvimento vegetativo' },
      { value: 'flowering', label: 'Floração' },
      { value: 'fruiting', label: 'Frutificação' },
      { value: 'harvest', label: 'Colheita' }
    ],
    locationTypeOptions: [
      { value: 'farm', label: 'Exploração agrícola' },
      { value: 'greenhouse', label: 'Estufa' },
      { value: 'field-site', label: 'Parcela de campo' },
      { value: 'agronomy-station', label: 'Estação agronómica' }
    ],
    areaTypeOptions: [
      { value: 'plot', label: 'Parcela' },
      { value: 'greenhouse-zone', label: 'Zona de estufa' },
      { value: 'crop-row', label: 'Linha de cultura' },
      { value: 'irrigation-sector', label: 'Setor de rega' },
      { value: 'storage-area', label: 'Área de armazenamento' }
    ],
    sensorTypeOptions: [
      { value: 'air_temperature', label: 'Temperatura do ar', unit: '°C' },
      { value: 'soil_temperature', label: 'Temperatura do solo', unit: '°C' },
      { value: 'soil_moisture', label: 'Humidade do solo', unit: '%' },
      { value: 'humidity', label: 'Humidade relativa', unit: '%' },
      { value: 'light', label: 'Luminosidade', unit: 'lux' },
      { value: 'rainfall', label: 'Precipitação', unit: 'mm' }
    ],
    categoryPhaseFallback: 'Cultura/fase por definir'
  },
  research: {
    type: 'research',
    label: 'Investigação',
    description: 'Pilotos, ensaios e observação experimental com configuração flexível.',
    areaLabel: 'Área experimental',
    areaCountSingularLabel: 'área',
    areaPluralLabel: 'áreas',
    subjectLabel: 'Objeto de estudo',
    categoryLabel: 'Categoria de estudo',
    phaseLabel: 'Fase experimental',
    phaseOptions: [
      { value: 'planning', label: 'Planeamento' },
      { value: 'preparation', label: 'Preparação' },
      { value: 'acquisition', label: 'Aquisição de dados' },
      { value: 'analysis', label: 'Análise' },
      { value: 'validation', label: 'Validação' },
      { value: 'completed', label: 'Concluído' }
    ],
    locationTypeOptions: [
      { value: 'research-centre', label: 'Centro de investigação' },
      { value: 'laboratory', label: 'Laboratório' },
      { value: 'experimental-facility', label: 'Instalação experimental' },
      { value: 'field-trial', label: 'Campo de ensaio' },
      { value: 'pilot-site', label: 'Local piloto' }
    ],
    areaTypeOptions: [
      { value: 'experimental-area', label: 'Área experimental' },
      { value: 'laboratory-bench', label: 'Bancada laboratorial' },
      { value: 'test-rig', label: 'Bancada de ensaio' },
      { value: 'sample-station', label: 'Estação de amostragem' },
      { value: 'observation-zone', label: 'Zona de observação' }
    ],
    sensorTypeOptions: [
      { value: 'temperature', label: 'Temperatura', unit: '°C' },
      { value: 'humidity', label: 'Humidade', unit: '%' },
      { value: 'pressure', label: 'Pressão', unit: 'bar' },
      { value: 'light', label: 'Luminosidade', unit: 'lux' },
      { value: 'co2', label: 'CO₂', unit: 'ppm' },
      { value: 'analog', label: 'Entrada analógica', unit: '' }
    ],
    categoryPhaseFallback: 'Categoria/fase por definir'
  }
};

export const normalizeOperationalContextType = (value: unknown): OperationalContextType => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return OPERATIONAL_CONTEXT_TYPES.includes(normalized as OperationalContextType)
    ? normalized as OperationalContextType
    : 'aquaculture';
};

export const getOperationalContextProfile = (value: unknown) =>
  OPERATIONAL_CONTEXT_PROFILES[normalizeOperationalContextType(value)];

export const OPERATIONAL_CONTEXT_OPTIONS = OPERATIONAL_CONTEXT_TYPES.map((type) => ({
  value: type,
  label: OPERATIONAL_CONTEXT_PROFILES[type].label
}));

export const getDefaultOperationalLocationType = (value: unknown) =>
  getOperationalContextProfile(value).locationTypeOptions[0]?.value ?? 'site';

export const getOperationalLocationTypeLabel = (contextType: unknown, locationType: unknown) => {
  const value = String(locationType ?? '').trim();
  return getOperationalContextProfile(contextType).locationTypeOptions.find((option) => option.value === value)?.label || value;
};
